/**
 * VH-131: the ONE equipment list.
 *
 * Before this, equipment appeared on two screens that could not agree: a
 * full-CRUD catalogue at /equipment, and a read-only grid on the location page
 * showing photo, name and exercise count. Equipment has been location-owned
 * since migration 074, so a free-floating catalogue contradicted the data model
 * — and the read-only grid could show you a machine without letting you fix it.
 *
 * One component, two scopes. Pass `locationId` and it is the inventory of that
 * place, with the place fixed. Omit it and it is every machine everywhere, with
 * a location filter and a location shown on each card.
 *
 * The scope is NOT a filter control when locked. VH-130's finding was that a
 * filter which looks adjustable but is not reads as "machines are hidden" —
 * which is what made Stage A look like data loss. A locked scope is stated as a
 * heading by the page above; this component simply never offers the dropdown.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { equipmentAPI } from '../../services/api'
import { trainingLocationsAPI } from '../../services/trainingLocations'
import {
  WrenchScrewdriverIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  PlayCircleIcon,
  LinkIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import EquipmentFormModal from './EquipmentFormModal'
import EquipmentPhotoActions from './EquipmentPhotoActions'
import AddEquipmentByPhoto from './AddEquipmentByPhoto'
import { contentLanguage } from '../../i18n/supported'

export default function EquipmentInventory({ locationId = null, locationName = '' }) {
  // A locked scope is the place's own page. The location filter is not merely
  // preset to it — it is not offered at all, so nothing on screen suggests
  // machines are being withheld.
  const scoped = locationId !== null && locationId !== undefined && locationId !== ''
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('') // CR-10: Debounced search
  const [selectedType, setSelectedType] = useState('')
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('')
  const [showAvailableOnly, setShowAvailableOnly] = useState(true)
  const [selectedUsageFilter, setSelectedUsageFilter] = useState('') // '', 'in_use', 'not_in_use'
  // VH-130: '' means every location. Deliberately the default -- a remembered
  // or forced filter hides machines, which reads as data loss.
  const [selectedLocation, setSelectedLocation] = useState('')
  // What the query actually filters on: the locked place when scoped, else
  // whatever the user picked (blank = every location).
  const effectiveLocation = scoped ? String(locationId) : selectedLocation
  const [showModal, setShowModal] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState(null)
  // VH-131: adding starts with a photo when we know the place, because
  // VH-126 Stage B always meant it to -- the AI names the machine and fills
  // the card, and the user corrects whatever it got wrong. Manual entry stays
  // one tap away for kit the AI cannot see: dumbbells, a mat, a bike.
  const [showPhotoStep, setShowPhotoStep] = useState(false)
  const [aiPrefill, setAiPrefill] = useState(null)
  const [translations, setTranslations] = useState({}) // Cache for translated equipment
  const [isTranslating, setIsTranslating] = useState(false) // Loading state for translations

  // CR-10: Debounce search term to prevent focus loss and continuous refresh
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // VH-130: the user's places, for the location filter.
  const { data: locationsData } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })
  const locations = locationsData?.locations || []

  // Fetch equipment (use limit: 100 to get all equipment, sorted alphabetically)
  // CR-10: Use debounced search term in query key to prevent re-renders on every keystroke
  const { data: equipmentData, isLoading } = useQuery({
    queryKey: ['equipment', debouncedSearchTerm, selectedType, selectedMuscleGroup, showAvailableOnly, effectiveLocation],
    queryFn: () => equipmentAPI.getAll({
      limit: 100,
      sort_by: 'name_en',
      search_term: debouncedSearchTerm || undefined,
      equipment_type: selectedType || undefined,
      muscle_group: selectedMuscleGroup || undefined,
      location_id: effectiveLocation || undefined,
      available_only: showAvailableOnly
    })
  })

  // Fetch translations for non-English languages
  useEffect(() => {
    let isMounted = true

    const fetchTranslations = async () => {
      if (currentLanguage === 'en' || !equipmentData?.data?.equipment) {
        setIsTranslating(false)
        return
      }

      const equipmentList = equipmentData.data.equipment
      setIsTranslating(true)

      // Fetch all translations in parallel
      const results = await Promise.allSettled(
        equipmentList.map(async (item) => {
          const cacheKey = `${item.id}-${currentLanguage}`
          try {
            const response = await equipmentAPI.translate(item.id, currentLanguage)
            return { cacheKey, data: response.data }
          } catch (error) {
            // Translation failed, will fall back to English
            return null
          }
        })
      )

      if (!isMounted) return

      // Update translations with fetched data
      const newTranslations = {}
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          newTranslations[result.value.cacheKey] = result.value.data
        }
      }

      if (Object.keys(newTranslations).length > 0) {
        setTranslations(prev => ({ ...prev, ...newTranslations }))
      }
      setIsTranslating(false)
    }

    fetchTranslations()

    return () => { isMounted = false }
  }, [equipmentData, currentLanguage])

  // VH-142: "In Exercise (N)" states a number but not WHICH exercises — the
  // question the badge itself provokes, and the one you need answered before
  // deleting a machine or judging whether it earns its space. Clicking it opens
  // the list. Fetched lazily: most cards are never opened, so listing eagerly
  // would be one request per machine for data usually unread.
  const [expandedUsageId, setExpandedUsageId] = useState(null)
  const { data: usageExercises, isLoading: isUsageLoading } = useQuery({
    queryKey: ['equipmentExercises', expandedUsageId],
    queryFn: async () => {
      const r = await equipmentAPI.getUsedByExercises(expandedUsageId)
      return r.data
    },
    enabled: expandedUsageId !== null,
    staleTime: 60_000,
  })

  // Helper function to get translated or original value
  const getTranslatedValue = (item, field) => {
    if (currentLanguage === 'en') {
      if (field === 'name') return item.name_en
      if (field === 'notes') return item.notes_en
      if (field === 'location') return item.location_in_gym
      return item[field]
    }
    const cacheKey = `${item.id}-${currentLanguage}`
    const translated = translations[cacheKey]
    if (translated) {
      return translated[field]
    }
    // Fallback to English while translation loads
    if (field === 'name') return item.name_en
    if (field === 'notes') return item.notes_en
    if (field === 'location') return item.location_in_gym
    return item[field]
  }

  // Fetch equipment types
  const { data: typesData } = useQuery({
    queryKey: ['equipmentTypes'],
    queryFn: equipmentAPI.getTypes
  })

  // Fetch muscle groups
  const { data: muscleGroupsData } = useQuery({
    queryKey: ['muscleGroups'],
    queryFn: equipmentAPI.getMuscleGroups
  })

  // Delete mutation
  // CR-09: Use predicate function to invalidate all equipment queries regardless of filters
  const deleteMutation = useMutation({
    mutationFn: ({ id, force }) => equipmentAPI.delete(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] })
    }
  })

  const handleDelete = (item, e) => {
    if (e) e.stopPropagation()
    const exerciseCount = item.usage?.exercise_count || 0

    if (exerciseCount > 0) {
      // Equipment is used by exercises - show usage warning
      const usageInfo = `${t('equipment.inUseWarning')}\n\n${t('nav.exercises')}: ${exerciseCount}`
      if (!confirm(usageInfo + '\n\n' + t('equipment.confirmDeleteInUse'))) return
      deleteMutation.mutate({ id: item.id, force: true })
    } else {
      if (confirm(t('equipment.confirmDelete'))) {
        deleteMutation.mutate({ id: item.id, force: false })
      }
    }
  }

  const handleEdit = (equipment) => {
    setEditingEquipment(equipment)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingEquipment(null)
    setAiPrefill(null)
    // Without a location there is nothing to scan against, so the All-equipment
    // view goes straight to the form -- where the location picker is required.
    if (scoped) {
      setShowPhotoStep(true)
    } else {
      setShowModal(true)
    }
  }

  const handleCandidate = ({ candidate }) => {
    setShowPhotoStep(false)
    setAiPrefill(candidate)
    setShowModal(true)
  }

  const handleAlreadyHere = (equipmentId) => {
    // VH-126: recognised, not re-created. Open the machine that is already
    // here rather than quietly making a second row with the same name.
    setShowPhotoStep(false)
    const existing = allEquipment.find((item) => item.id === equipmentId)
    if (existing) {
      setEditingEquipment(existing)
      setShowModal(true)
    }
  }

  const closeAdd = () => {
    setShowPhotoStep(false)
    setShowModal(false)
    setEditingEquipment(null)
    setAiPrefill(null)
  }

  const allEquipment = equipmentData?.data?.equipment || []
  const equipmentTypes = typesData?.data?.equipment_types || []
  const muscleGroups = muscleGroupsData?.data?.muscle_groups || []

  // Filter equipment by usage status (client-side filter since usage info is already included)
  const equipment = allEquipment.filter(item => {
    if (!selectedUsageFilter) return true
    const isInUse = item.usage?.exercise_count > 0
    if (selectedUsageFilter === 'in_use') return isInUse
    if (selectedUsageFilter === 'not_in_use') return !isInUse
    return true
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {scoped ? t('equipment.inventoryAt', { location: locationName }) : t('equipment.allEquipment')}
            </h1>
            <p className="mt-2 text-gray-600">
              {scoped ? t('equipment.inventorySubtitle') : t('equipment.subtitle')}
              {isTranslating && (
                <span className="ml-2 text-sm text-primary-600">
                  ({t('common.loading')}...)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('equipment.addEquipment')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('equipment.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        {/* Equipment Type Filter */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">{t('equipment.filters.allTypes')}</option>
          {equipmentTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {t(`equipment.types.${type.value}`, type.label)}
            </option>
          ))}
        </select>

        {/* Muscle Group Filter */}
        <select
          value={selectedMuscleGroup}
          onChange={(e) => setSelectedMuscleGroup(e.target.value)}
          className="focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">{t('equipment.filters.allMuscleGroups')}</option>
          {muscleGroups.map((mg) => (
            <option key={mg.value} value={mg.value}>
              {currentLanguage === 'de' ? mg.label_de : mg.label_en}
            </option>
          ))}
        </select>

        {/* VH-130: Location Filter. Hidden when the user has no places yet --
            an empty dropdown explains nothing. */}
        {!scoped && locations.length > 0 && (
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            data-testid="equipment-location-filter"
            className="focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">{t('equipment.filters.allLocations')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        )}

        {/* Usage Filter */}
        <select
          value={selectedUsageFilter}
          onChange={(e) => setSelectedUsageFilter(e.target.value)}
          className="focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">{t('common.all')}</option>
          <option value="in_use">{t('equipment.inUse')}</option>
          <option value="not_in_use">{t('equipment.notInUse')}</option>
        </select>
      </div>

      {/* Availability Toggle */}
      <div className="mb-6">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showAvailableOnly}
            onChange={(e) => setShowAvailableOnly(e.target.checked)}
            className="text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">
            {t('equipment.filters.showAvailableOnly')}
          </span>
        </label>
      </div>

      {/* Equipment List */}
      {equipment.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {equipment.map((item) => (
            <div
              key={item.id}
              className="card hover:shadow-lg transition-shadow"
            >
              <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <WrenchScrewdriverIcon className="h-6 w-6 text-primary-600 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {getTranslatedValue(item, 'name')}
                      </h3>
                    </div>
                    {/* In Use badge - shows when equipment is used by exercises */}
                    {item.usage?.exercise_count > 0 && (
                      <div className="mt-1 ml-8">
                        <button
                          type="button"
                          data-testid={`equipment-usage-toggle-${item.id}`}
                          aria-expanded={expandedUsageId === item.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            setExpandedUsageId(expandedUsageId === item.id ? null : item.id)
                          }}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                        >
                          <LinkIcon className="h-3 w-3 mr-1" />
                          {t('equipment.inUse')} ({item.usage.exercise_count})
                          {expandedUsageId === item.id ? (
                            <ChevronUpIcon className="h-3 w-3 ml-1" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3 ml-1" />
                          )}
                        </button>

                        {expandedUsageId === item.id && (
                          <div
                            data-testid={`equipment-usage-list-${item.id}`}
                            className="mt-2 border border-outline bg-surface-container px-3 py-2"
                          >
                            {isUsageLoading ? (
                              <p className="text-xs text-gray-500">{t('equipment.loadingExercises')}</p>
                            ) : usageExercises?.exercises?.length ? (
                              <ul className="space-y-1">
                                {usageExercises.exercises.map((ex) => (
                                  <li key={ex.id} className="text-xs text-gray-700 flex items-center">
                                    <span className="mr-1 text-gray-400">•</span>
                                    {(currentLanguage === 'de' ? ex.name_de : ex.name_en) ||
                                      ex.name_en ||
                                      ex.name_de}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-gray-500">{t('equipment.noExercisesUsing')}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {item.available_at_gym ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" title={t('equipment.availableAtGym')} />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-red-500" title={t('equipment.notAvailable')} />
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">{t('equipment.labels.type')}:</span>
                    <span className="ml-2 text-gray-600">
                      {t(`equipment.types.${item.equipment_type}`, item.equipment_type)}
                    </span>
                  </div>

                  {item.primary_muscle_groups && item.primary_muscle_groups.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">{t('equipment.labels.muscleGroups')}:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.primary_muscle_groups.map((mg) => (
                          <span
                            key={mg}
                            className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs"
                          >
                            {t(`equipment.muscleGroups.${mg}`, mg)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* VH-130: the PLACE this machine is at. Distinct from
                      `location_in_gym` below, which is where in that place it
                      stands ("back left corner"). */}
                  {!scoped && item.location_name && (
                    <div>
                      <span className="font-medium text-gray-700">{t('equipment.labels.trainingLocation')}:</span>
                      <span className="ml-2 text-gray-600" data-testid="equipment-location-name">
                        {item.location_name}
                      </span>
                    </div>
                  )}

                  {item.location_in_gym && (
                    <div>
                      <span className="font-medium text-gray-700">{t('equipment.labels.location')}:</span>
                      <span className="ml-2 text-gray-600">
                        {t(`equipment.locations.${item.location_in_gym}`, item.location_in_gym)}
                      </span>
                    </div>
                  )}

                  {item.video_url && (
                    <div>
                      <a
                        href={item.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-red-600 hover:text-red-700"
                      >
                        <PlayCircleIcon className="h-5 w-5 mr-1" />
                        <span>{t('equipment.watchTutorial')}</span>
                      </a>
                    </div>
                  )}

                  {item.default_weight_notation && (
                    <div>
                      <span className="font-medium text-gray-700">{t('equipment.labels.weightNotation')}:</span>
                      <span className="ml-2 text-gray-600">
                        {t(`equipment.weightNotations.${item.default_weight_notation}`, item.default_weight_notation)}
                      </span>
                    </div>
                  )}

                  {getTranslatedValue(item, 'notes') && (
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-gray-600 text-xs italic">
                        {getTranslatedValue(item, 'notes')}
                      </p>
                    </div>
                  )}
                </div>

                {/* VH-131: the photo, and the way to change it. The grey
                    placeholder is deliberate — on a phone a column of them is
                    exactly what tells you which machines still need a picture. */}
                <EquipmentPhotoActions equipment={item} />

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="btn-outline btn-sm flex items-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={(e) => handleDelete(item, e)}
                    className={`btn-outline btn-sm flex items-center ${
                      item.usage?.exercise_count > 0
                        ? 'text-blue-600 hover:bg-blue-50'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {scoped
              ? t('equipment.noEquipmentAtLocation', { location: locationName })
              : selectedLocation
                ? t('equipment.noEquipmentAtLocation', {
                    location: locations.find((l) => String(l.id) === String(selectedLocation))?.name || '',
                  })
                : t('equipment.noEquipment')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {!searchTerm && !selectedType && !selectedMuscleGroup && !selectedLocation && t('equipment.noEquipmentDescription')}
          </p>
          {/* VH-130: an empty list caused by a filter must say so, and offer
              the way back. A silent blank screen is what made a migration look
              like data loss. */}
          {!scoped && selectedLocation && (
            <button
              onClick={() => setSelectedLocation('')}
              data-testid="clear-location-filter"
              className="mt-3 text-sm text-primary-600 hover:text-primary-700 underline"
            >
              {t('equipment.showAllLocations')}
            </button>
          )}
          {!searchTerm && !selectedType && !selectedMuscleGroup && (
            <div className="mt-6">
              <button onClick={handleAdd} className="btn-primary">
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('equipment.addEquipment')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* VH-131: the photo step that precedes the form. */}
      {showPhotoStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-surface-container p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {t('equipment.addEquipment')}
            </h2>
            <AddEquipmentByPhoto
              locationId={locationId}
              onCandidate={handleCandidate}
              onAlreadyHere={handleAlreadyHere}
              onManual={() => { setShowPhotoStep(false); setShowModal(true) }}
              onCancel={closeAdd}
            />
          </div>
        </div>
      )}

      {/* Equipment Form Modal */}
      {showModal && (
        <EquipmentFormModal
          equipment={editingEquipment}
          defaultLocationId={scoped ? locationId : null}
          aiPrefill={aiPrefill}
          equipmentTypes={equipmentTypes}
          muscleGroups={muscleGroups}
          onClose={closeAdd}
          onSuccess={() => {
            // CR-09: Use predicate to invalidate all equipment queries regardless of active filters
            queryClient.invalidateQueries({ queryKey: ['equipment'] })
            queryClient.invalidateQueries({ queryKey: ['training-locations'] })
            closeAdd()
          }}
        />
      )}
    </div>
  )
}

